import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { usePmt3ProblemsDashboardData } from '@/hooks/usePmt3ProblemsDashboardData'; // Reusing hook
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { DocumentData } from 'firebase/firestore';
import { calculatePricePerPiece } from '@/utils/pmt3Calculations';
import { ArrowUpDown } from 'lucide-react';

// --- Types and Constants ---
type SortDirection = 'asc' | 'desc';
type FamilyMember = DocumentData & {
    PricePerPiece?: number | null;
};
type SortKey = keyof FamilyMember | string;

// --- Formatting Helpers (Updated versions) ---
const parseSafeFloat = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
    const num = parseFloat(cleanedValue);
    return isNaN(num) ? null : num;
};

// Helper specifically for parsing percentages that might be stored as "X%" strings or decimals
// Returns the value as a decimal (e.g., 55% -> 0.55, 0.55 -> 0.55)
const parsePercentAsDecimal = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    let num: number | null = null;
    const stringValue = String(value);
    if (stringValue.includes('%')) {
        num = parseSafeFloat(stringValue); // Handles "X%" -> X
        if (num !== null) {
            num = num / 100; // Convert to decimal
        }
    } else {
        num = parseSafeFloat(stringValue); // Assumes it's already a decimal if no % sign
    }
    return num;
}

const formatCurrency = (value: any, decimals = 0): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

// Formats a decimal value (e.g., 0.25) into a percentage string ("25%")
const formatDecimalAsPercent = (value: any): string => {
    const num = parseSafeFloat(value); // Input should be a decimal
    if (num === null) return '-';
    return `${Math.round(num * 100)}%`;
};

// Formats a number that already represents a percentage (e.g., 55 for 55%) into a string ("55%")
const formatDirectPercent = (value: any): string => {
    const num = parseSafeFloat(value); // Input might be "55%" or 55
    if (num === null) return '-';
    return `${Math.round(num)}%`; // Just round and add %
}

const formatNumber = (value: any, decimals = 0): string => {
    const num = parseSafeFloat(value);
    if (num === null) return '-';
     return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDefault = (value: any): string => {
    // Return '-' for null, undefined, or empty string
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};
// --- End Formatting Helpers ---


const FamilySituationDashboard: React.FC = () => {
    const { selectedProduct } = usePMT3Selection();
    // Assuming the hook fetches all product data for the company
    const { data, isLoading, error } = usePmt3ProblemsDashboardData(selectedProduct?.companyName);

    // State for sorting
    const [sortBy, setSortBy] = useState<SortKey>('id');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Find the currently selected product's data
    const currentProductData = data?.productData?.find(p => p.id === selectedProduct?.id);
    // Get the family name, treat empty string or null/undefined as "Not Set" for logic purposes
    const familyName = currentProductData?.['Product Family Name'] as string | undefined;
    const isFamilyNotSet = !familyName || familyName === 'Not Set';

    // Data augmentation and sorting logic
    const processedFamilyMembers = useMemo(() => {
        if (!data?.productData) return [];

        // 1. Augment data with PricePerPiece and filter by family
        const augmentedMembers: FamilyMember[] = data.productData
            .filter(p => familyName && familyName !== 'Not Set' && p['Product Family Name'] === familyName)
            .map(member => {
                const price = member['Price'];
                const title = member['Product Name'] as string;
                
                // Calculate Price Per Piece
                const pricePerPiece = calculatePricePerPiece(price, title);

                return {
                    ...member,
                    PricePerPiece: pricePerPiece,
                };
            });

        // 2. Implement sorting
        const sortedMembers = [...augmentedMembers].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];

            // Handle null/undefined values for sorting
            if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
            if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

            // Numeric comparison
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // String comparison (case-insensitive)
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
            if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return sortedMembers;
    }, [data, currentProductData, sortBy, sortDirection, familyName]);

    const familyMembers = processedFamilyMembers;

    // Determine header based on whether family is set
    const firstColumnHeader = isFamilyNotSet ? 'Not Set' : `${familyName} (${familyMembers.length})`;

    // Define toggle sort function
    const handleSort = (key: SortKey) => {
        if (sortBy === key) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDirection('asc');
        }
    };

    // Define field mappings and formatting for table cells
    const fieldConfig: { field: SortKey; label: string; format: (val: any) => string; isSortable: boolean }[] = [
        { field: 'id', label: 'ASIN', format: formatDefault, isSortable: true }, // ASIN
        { field: 'Product Name', label: 'Product Name', format: formatDefault, isSortable: true },
        { field: 'PPC Status', label: 'PPC Status', format: formatDefault, isSortable: true },
        { field: 'Price', label: 'Price', format: (val) => formatCurrency(val, 2), isSortable: true },
        { field: 'PricePerPiece', label: 'Price Per Piece', format: (val) => formatCurrency(val, 2), isSortable: true }, // NEW COLUMN
        { field: 'Gross Profit', label: 'Gross Profit', format: (val) => formatCurrency(val, 2), isSortable: true },
        { field: 'Gross Profit Margin', label: 'Gross Profit Margin', format: formatDecimalAsPercent, isSortable: true },
        { field: 'DSR', label: 'DSR', format: (val) => formatNumber(val, 1), isSortable: true },
        { field: 'Days of Cover', label: 'Days of Cover', format: (val) => formatNumber(val, 0), isSortable: true },
        { field: '28 Day Gross Profit', label: '28 Day Gross Profit', format: (val) => formatCurrency(val, 0), isSortable: true },
    ];

    // Define headers dynamically
    const headers = [
        { field: 'id', label: firstColumnHeader, isSortable: true }, // Use dynamic header for the first column (ASIN)
        ...fieldConfig.slice(1).map(fc => ({ field: fc.field, label: fc.label, isSortable: fc.isSortable }))
    ];


    if (error) {
        return <Card><CardHeader><CardTitle className="text-2xl font-bold">Family Situation</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error loading data: {error.message}</p></CardContent></Card>;
    }

     if (!selectedProduct && !isLoading) {
         return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Family Situation</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Family Situation.</p></CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="p-4">
                <CardTitle className="text-2xl font-bold">Family Situation</CardTitle>
            </CardHeader>
            <CardContent className="p-4 overflow-x-auto">
                <Table className="min-w-full">
                    <TableHeader>
                        <TableRow>
                            {headers.map(header => (
                                <TableHead
                                    key={header.field as string}
                                    className="py-2 px-3 text-xs whitespace-nowrap cursor-pointer hover:bg-gray-800/50 transition-colors"
                                    onClick={() => header.isSortable && handleSort(header.field)}
                                >
                                    <div className="flex items-center">
                                        {header.label}
                                        {header.isSortable && sortBy === header.field && (
                                            <ArrowUpDown className={`ml-2 h-3 w-3 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                                        )}
                                        {header.isSortable && sortBy !== header.field && (
                                            <ArrowUpDown className="ml-2 h-3 w-3 opacity-20" />
                                        )}
                                    </div>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            // Skeleton Row
                            <TableRow>
                                {headers.map((_, index) => (
                                     <TableCell key={`skel-${index}`} className="py-2 px-3">
                                         <Skeleton className="h-4 w-full" />
                                     </TableCell>
                                ))}
                            </TableRow>
                        ) : isFamilyNotSet ? (
                             // Render single row with "Not Set" and dashes if family name is not set
                             <TableRow>
                                 <TableCell className="py-2 px-3 text-xs whitespace-nowrap font-medium">Not Set</TableCell>
                                 {/* Render dashes for the remaining columns */}
                                 {Array.from({ length: headers.length - 1 }).map((_, index) => (
                                     <TableCell key={`placeholder-${index}`} className="py-2 px-3 text-xs">-</TableCell>
                                 ))}
                             </TableRow>
                        ) : familyMembers.length > 0 ? (
                            // Render actual family members if family name is set and members exist
                            <>
                                {familyMembers.map((member) => (
                                    <TableRow key={member.id}>
                                        {fieldConfig.map((config) => (
                                            <TableCell key={`${member.id}-${config.field as string}`} className="py-2 px-3 text-xs whitespace-nowrap">
                                                {/* Use bracket notation for safety */}
                                                {config.format(member[config.field])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                {/* Padding row to separate content from scrollbar */}
                                <TableRow>
                                    <TableCell colSpan={headers.length} className="py-4 px-3 h-4 border-b-0"></TableCell>
                                </TableRow>
                            </>
                        ) : (
                            // Render message if family name is set but no members found (should be rare if selectedProduct is one)
                            <TableRow>
                                <TableCell colSpan={headers.length} className="text-center text-sm text-gray-500 py-4">
                                    No other products found in this family.
                                </TableCell>
                            </TableRow>
                         )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

export default FamilySituationDashboard;